import { Component } from 'preact';
import { createPortal } from 'preact/compat';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchInbox, sendMail, claimMail, deleteMail, markMailRead } from '../utils/arena/mailApi';
import SeasonVoucherPickerModal from './SeasonVoucherPickerModal';
import { loadArenaProfile } from '../utils/arena/profileApi';
import { refreshMailbox } from '../utils/presenceManager';
import { playUI, UI } from '../utils/arena/uiSounds';
import CardInspector from './CardInspector';
import { InspectorHoldTracker } from '../utils/inspectorHold';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import VikingOrnament from './VikingOrnament';
import MarkdownNotes from './MarkdownNotes';
import ChatConversationList from './ChatConversationList';
import GlobalChatView from './GlobalChatView';
import NotificationsFeed from './NotificationsFeed';
import { Select } from './ui/select';
import { CoinIcon, KeystoneIcon } from './ui/icons';
import { declineDraftEvent, keystoneCostForMode } from '../utils/arena/draftApi';
import { BackButton, CloseButton } from './ui/navButtons';
import { resolvePrinting } from '../utils/sorcery/foil';
import {
  ART_GROUP_BASE, printingsInArtGroup, artGroupLabel, artGroupDisplayName,
} from '../utils/sorcery/artGroups';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD,
  PANEL_BG, GOLD_BTN, BEVELED_BTN, DANGER_BTN,
  TAB_ACTIVE, TAB_INACTIVE, COIN_COLOR,
  OPAQUE_TEXTURED_PANEL_STYLE,
  FourCorners, OrnamentalDivider,
} from '../lib/medievalTheme';
import { TabBar } from './ui/TabBar';
import { k, t } from '../localization/t';

// The mailbox dialog has a centerpiece Viking ornament rendered behind
// its content. Most shared theme styles use semi-transparent backgrounds
// (e.g. INPUT_STYLE = rgba(0,0,0,0.25), GOLD_BTN gradient at 0.85-0.9
// alpha), which let the ornament strokes bleed through any input or
// button rendered on top. To fix that, we layer an opaque base color
// underneath the existing styles by setting backgroundColor AFTER the
// spread of the shared style. The CSS `background` shorthand resets
// background-color to transparent; the explicit backgroundColor that
// follows reinstates a fully opaque base layer that the gradients and
// textures composite on top of, hiding the ornament behind them.
const SOLID_BASE = '#0e0a06';
const SOLID_GOLD_BASE = '#3a2812';

// ── Concentric radii ───────────────────────────────────────
// Same ladder the Notifications menu and the audio mixer popover use, so
// the three floating panels read as one material:
//
//   panel 1.2cqw ─ padding 0.7cqw ─→ rows / fields 0.5cqw
//   rows are padded wider than their radius, so controls take 0.25cqw
const PANEL_RADIUS = 'var(--radius-2xl)';
const PANEL_PADDING = '0.5cqw';
const ROW_RADIUS = 'var(--radius-lg)';
const ROW_PADDING_X = '0.625cqw';
// Leading disc on a row. Sized and ringed like the Notifications menu's
// avatar: a white hairline, not a gold rim, so the ring reads as the
// edge of the image rather than as more panel chrome.
const AVATAR_SIZE = '2.25cqw';
const AVATAR_RING = 'inset 0 0 0 1px rgba(255,255,255,0.08)';

// Medieval "carved stone well" input style. Uses the project's shared
// stone / chisel / scratches / cracks textures on top of a deep dark
// base, with a thin gold rim and a heavy inset shadow so the field
// reads as if it were chiselled INTO the panel rather than sitting on
// top of it. Blend modes keep the textures subtle. You want to feel
// the surface, not see the tiles. Both <input> and <textarea> use this.
const MAILBOX_INPUT_STYLE = {
  backgroundImage: [
    "url('/tex-stone.webp')",
    "url('/tex-chisel.webp')",
    "url('/tex-scratches.webp')",
    "url('/tex-cracks.webp')",
    'linear-gradient(180deg, rgba(18,12,6,0.98) 0%, rgba(8,5,2,0.98) 100%)',
  ].join(', '),
  backgroundBlendMode: 'soft-light, soft-light, overlay, multiply, normal',
  backgroundColor: SOLID_BASE,
  backgroundSize: '260px, 320px, 200px, 380px, 100% 100%',
  backgroundRepeat: 'repeat, repeat, repeat, repeat, no-repeat',
  border: `1px solid ${GOLD} 0.32)`,
  borderRadius: 'var(--radius-md)',
  color: TEXT_PRIMARY,
  boxShadow: [
    'inset 0 3px 10px rgba(0,0,0,0.85)',
    'inset 0 -1px 0 rgba(212,168,67,0.10)',
    'inset 0 0 0 1px rgba(0,0,0,0.35)',
    '0 1px 0 rgba(212,168,67,0.06)',
  ].join(', '),
  textShadow: '0 1px 1px rgba(0,0,0,0.55)',
  fontFamily: 'inherit',
};
const MAILBOX_GOLD_BTN = { ...GOLD_BTN, backgroundColor: SOLID_GOLD_BASE };
const MAILBOX_BEVELED_BTN = { ...BEVELED_BTN, backgroundColor: SOLID_BASE };
const MAILBOX_DANGER_BTN = { ...DANGER_BTN, backgroundColor: SOLID_BASE };
// TAB_ACTIVE/TAB_INACTIVE both use mostly-transparent backgrounds
// (rgba(GOLD, 0.12) and `transparent` respectively). Add an opaque base
// underneath so the centerpiece ornament can't bleed through the tab bar.
const MAILBOX_TAB_ACTIVE = { ...TAB_ACTIVE, backgroundColor: SOLID_GOLD_BASE };
const MAILBOX_TAB_INACTIVE = { ...TAB_INACTIVE, backgroundColor: SOLID_BASE };

// Attachments carry the collection identity (card, art group, foiling), so
// the art group picks the illustration. Otherwise an alt-art attachment
// previews as the booster art the sender isn't actually sending.
function resolveCardImage(cardId, artGroup, sorceryCards) {
  if (!cardId || !sorceryCards) return null;
  const card = sorceryCards.find(c => c.unique_id === cardId);
  if (!card) return null;
  const printing = printingsInArtGroup(card, artGroup || ART_GROUP_BASE)[0] || card.printings?.[0];
  return printing?.image_url || null;
}

function resolveCardName(cardId, artGroup, sorceryCards) {
  if (!cardId || !sorceryCards) return cardId;
  const card = sorceryCards.find(c => c.unique_id === cardId);
  if (!card) return cardId;
  return artGroupDisplayName(card, artGroup || ART_GROUP_BASE) || cardId;
}

function timeAgo(timestamp) {
  const date = typeof timestamp === 'number' ? timestamp : new Date(timestamp).getTime();
  if (isNaN(date)) return '';
  const diff = Date.now() - date;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t('Just now');
  if (mins < 60) return t('{n}m ago', { n: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('{n}h ago', { n: hours });
  const days = Math.floor(hours / 24);
  if (days < 30) return t('{n}d ago', { n: days });
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Labels only. The rail used to pair each label with a 14px outline
// icon, which at three tabs across a 380px panel read as chrome
// competing with the list underneath rather than as navigation.
const TAB_KEYS = [
  { key: 'friend', label: k('Messages') },
  // Global chat. Same view the hub's right panel shows, so it stays
  // reachable from a match or the deck builder.
  { key: 'global', label: k('Tavern') },
  // Match invites, lobbies, activity and every system letter (auction
  // payouts, season rewards, news) in one feed. Internal mail types
  // stay as they are server-side; only the surface merged.
  { key: 'notifications', label: k('Notifications') },
];

export default class Mailbox extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tab: 'friend',
      view: props.initialView === 'compose' ? 'compose' : 'list',
      mail: [],
      loading: true,
      selectedMail: null,
      claiming: false,
      sending: false,
      composeRecipient: null,
      composeSubject: '',
      composeBody: '',
      composeCards: [],
      composeCoins: 0,
      showCardPicker: false,
      cardPickerSearch: '',
      pickerHoveredEntry: null,
      inspectedCard: null,
      error: null,
      // Chat state. Friend tab
      chatFriend: null, // { friendId, friendName, friendAvatar, online }
      // When non-null, the picker modal is open for this voucher mail
      // id. Closing the picker clears the field. The picker handles
      // its own fetch + redeem flow; on success it calls back with the
      // mail id so we can flip the mail to claimed locally.
      voucherPickerMailId: null,
    };
    this.chatViewRef = null;
    this.chatListRef = null;
    this.inspectorHold = new InspectorHoldTracker();
  }

  componentDidMount() {
    window.addEventListener('keydown', this.handlePickerKeyDown);
    window.addEventListener('keyup', this.handlePickerKeyUp);
    this.loadInbox();
  }

  componentWillUnmount() {
    this._unmounted = true;
    window.removeEventListener('keydown', this.handlePickerKeyDown);
    window.removeEventListener('keyup', this.handlePickerKeyUp);
    removeDismiss(this._escDismissId);
  }

  componentDidUpdate(prevProps, prevState) {
    // Space-was-armed → hover landed on a card → open inspector now.
    if (
      this.state.showCardPicker &&
      !this.state.inspectedCard &&
      this.state.pickerHoveredEntry &&
      this.state.pickerHoveredEntry !== prevState.pickerHoveredEntry &&
      this.inspectorHold.consumePending()
    ) {
      playUI(UI.INSPECTOR_OPEN);
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ inspectedCard: this.state.pickerHoveredEntry });
    }

    // Deep-link arriving while already mounted (the Mailbox is never
    // unmounted, so loadInbox's first-mount resolution alone would
    // miss it). Re-run the same lookup against a fresh inbox.
    if (this.props.selectedMailId && this.props.selectedMailId !== prevProps.selectedMailId) {
      this.loadInbox();
    }

    if (!prevProps.open && this.props.open) {
      this.loadInbox();
      // Always resync the chat conversation list on open. The `chat:
      // message` WS push is fire-and-forget, so an invite sent while
      // the recipient was offline/backgrounded would otherwise leave
      // this list stale until they switch tabs or restart.
      this.chatListRef?.refresh?.();
    }
    // A chat deep link lands on the friend tab whether the drawer was
    // closed (profile "Send message") or already open (a "New message
    // from X" row on the Notifications tab).
    if (this.props.open && this.props.chatFriend
        && (!prevProps.open || this.props.chatFriend !== prevProps.chatFriend)) {
      this.setState({ tab: 'friend', view: 'list', selectedMail: null, chatFriend: this.props.chatFriend });
    }

    // Notification deep link into the Tavern: switch tabs, the view
    // scrolls to and highlights the message itself.
    if (this.props.focusGlobalMessageId && this.props.focusGlobalMessageId !== prevProps.focusGlobalMessageId) {
      this.setState({ tab: 'global', view: 'list', selectedMail: null, chatFriend: null });
    }
    if (this.props.focusNotifications && this.props.focusNotifications !== prevProps.focusNotifications) {
      this.setState({ tab: 'notifications', view: 'list', selectedMail: null, chatFriend: null });
    }
    // Route incoming chat WS events to the active chat view
    if (this.props.lastChatMessage && this.props.lastChatMessage !== prevProps.lastChatMessage) {
      const msg = this.props.lastChatMessage;
      if (this.chatViewRef && this.state.chatFriend?.friendId === msg.senderId) {
        this.chatViewRef.receiveMessage(msg);
      } else if (this.chatListRef) {
        this.chatListRef.refresh();
      }
    }
    if (this.props.lastChatClaimed && this.props.lastChatClaimed !== prevProps.lastChatClaimed) {
      if (this.chatViewRef) {
        this.chatViewRef.handleClaimed(this.props.lastChatClaimed);
      }
    }

    const inspectorOpen = this.state.inspectedCard != null;
    const wasInspectorOpen = prevState.inspectedCard != null;
    if (inspectorOpen && !wasInspectorOpen) {
      this._escDismissId = pushDismiss(() => {
        playUI(UI.INSPECTOR_CLOSE);
        this.setState({ inspectedCard: null });
      });
    } else if (!inspectorOpen && wasInspectorOpen) {
      removeDismiss(this._escDismissId);
    }
  }

  handlePickerKeyDown = (e) => {
    if (e.repeat) return;
    const tag = e.target?.tagName?.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable) return;

    if ((e.key === ' ' || e.code === 'Space') && this.state.showCardPicker) {
      e.preventDefault();
      if (this.state.inspectedCard) {
        playUI(UI.INSPECTOR_CLOSE);
        this.inspectorHold.noteClose();
        this.setState({ inspectedCard: null });
      } else if (this.state.pickerHoveredEntry) {
        playUI(UI.INSPECTOR_OPEN);
        this.inspectorHold.noteOpen();
        this.setState({ inspectedCard: this.state.pickerHoveredEntry });
      } else {
        // No card hovered yet. Arm so the next hover opens.
        this.inspectorHold.noteArmed();
      }
    }
  };

  // Releasing Space closes the inspector only if it was held past the
  // hold threshold; quick taps leave it open (tap-toggle mode).
  handlePickerKeyUp = (e) => {
    if (e.key !== ' ' && e.code !== 'Space') return;
    if (!this.state.showCardPicker) return;
    // Tracker first: releasing Space also disarms a hover-less press.
    if (!this.inspectorHold.shouldCloseOnKeyUp() || !this.state.inspectedCard) return;
    e.preventDefault();
    playUI(UI.INSPECTOR_CLOSE);
    this.setState({ inspectedCard: null });
  };

  loadInbox = async () => {
    this.setState({ loading: true, error: null });
    try {
      const result = await fetchInbox();
      // Server returns an array directly; older versions wrapped it in {mail: [...]}
      const mail = Array.isArray(result) ? result : (result?.mail || []);
      const nextState = { mail, loading: false };

      if (this.props.selectedMailId) {
        const found = mail.find(m => m.id === this.props.selectedMailId);
        if (found) {
          nextState.selectedMail = found;
          nextState.view = 'detail';
          nextState.tab = 'notifications';
          // Deep-linking to a mail counts as opening it, same as a
          // manual list click. Go through openDetail after the
          // list state settles so the mark-read side effect runs.
          if (!found.readAt) {
            setTimeout(() => this.openDetail(found), 0);
          }
        } else if (this.state.view === 'detail') {
          // Deep-link to a mail that is no longer in the inbox: fall
          // back to the list rather than leaving a stale letter open.
          nextState.selectedMail = null;
          nextState.view = 'list';
        }
      }

      if (this.props.initialView === 'compose') {
        nextState.view = 'compose';
        if (this.props.composeRecipientId && this.props.friendListData?.friends) {
          const friend = this.props.friendListData.friends.find(
            f => f.id === this.props.composeRecipientId
          );
          if (friend) nextState.composeRecipient = friend;
        }
      }

      this.setState(nextState);
    } catch (err) {
      this.setState({ loading: false, error: err.message });
    }
  };

  openDetail = (mail) => {
    // Reset per-mail slot-claim state: the auto-delete window keeps the
    // previous mail's claimed slots around for ~2s, and without the reset
    // the next mail's attachments render as already collected.
    this.setState({ selectedMail: mail, view: 'detail', claimedSlots: new Set() });
    // First-open marks the mail as read server-side; subsequent
    // opens are a no-op. Optimistically patch the local list so the
    // badge math below re-runs without waiting for the round-trip,
    // then refresh the shared summary so the header + tabs drop the
    // unread count immediately.
    if (!mail?.readAt && mail?.id) {
      const stampedAt = new Date().toISOString();
      this.setState((s) => ({
        mail: s.mail.map((m) => (m.id === mail.id ? { ...m, readAt: stampedAt } : m)),
        selectedMail: s.selectedMail?.id === mail.id ? { ...s.selectedMail, readAt: stampedAt } : s.selectedMail,
      }));
      markMailRead(mail.id)
        .then(() => refreshMailbox())
        .catch(() => {});
    }
  };

  backToList = () => {
    this.setState({
      view: 'list',
      selectedMail: null,
      error: null,
      claimedSlots: new Set(),
    });
  };

  openCompose = (recipient) => {
    this.setState({
      view: 'compose',
      composeRecipient: recipient || null,
      composeSubject: '',
      composeBody: '',
      composeCards: [],
      composeCoins: 0,
      showCardPicker: false,
      error: null,
    });
  };

  openReply = (mail) => {
    const friends = this.props.friendListData?.friends || [];
    const sender = friends.find(f => f.id === mail.senderId);
    this.setState({
      view: 'compose',
      composeRecipient: sender || { id: mail.senderId, name: mail.senderName },
      composeSubject: mail.subject?.startsWith('Re: ') ? mail.subject : `Re: ${mail.subject || ''}`,
      composeBody: '',
      composeCards: [],
      composeCoins: 0,
      showCardPicker: false,
      error: null,
    });
  };

  handleSlotCollect = (mail, slotKey, animData, allCards, hasCoins) => {
    playUI(UI.MAIL_COLLECT);
    const claimedSlots = new Set(this.state.claimedSlots || []);
    claimedSlots.add(slotKey);

    this.setState({ claimedSlots, collectAnim: animData });
    clearTimeout(this._collectAnimTimer);
    this._collectAnimTimer = setTimeout(() => this.setState({ collectAnim: null }), 1800);

    // Check if all slots are now collected
    const allCardsClaimed = allCards.every((_, i) => claimedSlots.has(`card-${i}`));
    const coinsClaimed = !hasCoins || claimedSlots.has('coins');
    if (allCardsClaimed && coinsClaimed) {
      // Auto-claim after animation starts
      setTimeout(() => {
        this.handleClaim(mail);
      }, 600);
    }
  };

  handleClaim = async (mail) => {
    this.setState({ claiming: true, error: null });
    try {
      await claimMail(mail.id);

      // Reload the full profile from the server to pick up updated coins + collection
      const token = this.props.profile?.serverToken;
      if (token && this.props.onProfileUpdate) {
        const fresh = await loadArenaProfile(token).catch(() => null);
        if (fresh) {
          this.props.onProfileUpdate({
            ...this.props.profile,
            coins: fresh.coins ?? this.props.profile.coins,
            collection: fresh.collection ?? this.props.profile.collection,
          });
        }
      }

      // Auto-delete auction mail after a short delay
      const isAuction = mail.type === 'auction';
      const updatedMail = this.state.mail.map((m) =>
        m.id === mail.id ? { ...m, claimed: true } : m,
      );
      this.setState({
        mail: updatedMail,
        selectedMail: { ...mail, claimed: true },
        claiming: false,
      });

      // Refresh the mailbox unread badge in the parent
      refreshMailbox();

      if (isAuction) {
        setTimeout(async () => {
          await deleteMail(mail.id).catch(() => {});
          const remaining = this.state.mail.filter((m) => m.id !== mail.id);
          this.setState({ mail: remaining, selectedMail: null, view: 'list', claimedSlots: new Set() });
          refreshMailbox();
        }, 2000);
      }
    } catch (err) {
      this.setState({ claiming: false, error: err.message });
    }
  };

  handleDelete = async (mail) => {
    playUI(UI.MAIL_DELETE);
    this.setState({ error: null });
    try {
      await deleteMail(mail.id);
      const updatedMail = this.state.mail.filter((m) => m.id !== mail.id);
      this.setState({ mail: updatedMail, view: 'list', selectedMail: null });
      refreshMailbox();
    } catch (err) {
      this.setState({ error: err.message });
    }
  };

  handleSend = async () => {
    const { composeRecipient, composeSubject, composeBody, composeCards, composeCoins } = this.state;
    if (!composeRecipient) return;

    // composeCards is an array of { cardId, foiling } items where each
    // entry represents one copy. Aggregate into the server's
    // { cardId, foiling, quantity } payload.
    const cardPayload = [];
    for (const entry of composeCards) {
      const existing = cardPayload.find(
        (c) => c.cardId === entry.cardId
          && c.artGroup === entry.artGroup
          && c.foiling === entry.foiling
      );
      if (existing) existing.quantity++;
      else cardPayload.push({ cardId: entry.cardId, artGroup: entry.artGroup, foiling: entry.foiling, quantity: 1 });
    }

    this.setState({ sending: true, error: null });
    try {
      await sendMail({
        recipientId: composeRecipient.id,
        subject: composeSubject,
        body: composeBody,
        cards: cardPayload.length > 0 ? cardPayload : undefined,
        coins: composeCoins > 0 ? composeCoins : undefined,
      });

      // Reload the full profile from the server so coins + collection
      // reflect the deductions. Avoids local recomputation drift.
      const token = this.props.profile?.serverToken;
      if (token && this.props.onProfileUpdate) {
        const fresh = await loadArenaProfile(token).catch(() => null);
        if (fresh) {
          this.props.onProfileUpdate({
            ...this.props.profile,
            coins: fresh.coins ?? this.props.profile.coins,
            collection: fresh.collection ?? this.props.profile.collection,
          });
        }
      }

      playUI(UI.MAIL_SEND);
      this.props.onSendComplete?.();
      this.setState({ sending: false, view: 'list' });
      this.loadInbox();
    } catch (err) {
      this.setState({ sending: false, error: err.message });
    }
  };

  addCardToCompose = (cardId, artGroup = ART_GROUP_BASE, foiling = 'S') => {
    this.setState(s => {
      if (s.composeCards.length >= 10) return null;
      return { composeCards: [...s.composeCards, { cardId, artGroup, foiling }] };
    });
  };

  removeCardFromCompose = (cardId, artGroup = ART_GROUP_BASE, foiling = 'S') => {
    this.setState(s => {
      const idx = s.composeCards.findLastIndex(
        (c) => c.cardId === cardId && c.artGroup === artGroup && c.foiling === foiling
      );
      if (idx < 0) return null;
      const next = [...s.composeCards];
      next.splice(idx, 1);
      return { composeCards: next };
    });
  };

  renderTabBar() {
    const { tab } = this.state;
    const unreadByTab = this.props.unreadByTab || { friend: 0, global: 0, notifications: 0 };
    return (
      <TabBar
        className="vh-panel-tabs vh-social-tabs shrink-0"
        activeIndex={TAB_KEYS.findIndex(t => t.key === tab)}
        // `underline` instead of `chapter`: the selection is a single
        // travelling gold bar and a warmer label, with no lit plate
        // behind it. At panel width the plate was the loudest thing on
        // screen and it boxed the active tab off from the list it
        // heads.
        variant="underline"
        barInset="26%"
        barStyle={{ bottom: '-1px' }}
      >
        {TAB_KEYS.map(item => {
          const active = tab === item.key;
          // Badge hides on the active tab. The user is already looking
          // at its contents, so announcing "N unread" there is noise.
          const unread = unreadByTab[item.key] || 0;
          const showBadge = !active && unread > 0;
          return (
            <button
              key={item.key}
              data-tab-item
              type="button"
              className={`vh-panel-tab ${active ? 'vh-panel-tab-active' : ''}`}
              onClick={() => {
                // Leaving the Tavern spends its deep link; otherwise the
                // view would jump to the same message on every return.
                if (tab === 'global' && item.key !== 'global') this.props.onTavernFocusDone?.();
                this.setState({ tab: item.key, view: 'list', selectedMail: null, error: null, chatFriend: null });
              }}
            >
              {t(item.label)}
              {showBadge ? (
                <span
                  className="vh-panel-tab-count tabular-nums"
                  style={{ background: `${GOLD} 0.2)`, color: ACCENT_GOLD }}
                >
                  {unread > 99 ? '99+' : unread}
                </span>
              ) : null}
            </button>
          );
        })}
      </TabBar>
    );
  }

  renderDraftInviteBody(mail) {
    let invite = null;
    try { invite = JSON.parse(mail.body); } catch { return null; }
    if (!invite) return null;

    const startTime = new Date(invite.scheduledAt).toLocaleString([], {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
    const accepted = this.state.draftInviteAccepted === mail.id;
    const accepting = this.state.draftInviteAccepting === mail.id;

    return (
      <div className="mb-3">
        <div className="text-xs leading-relaxed mb-3" style={{ color: TEXT_BODY }}>
          <span className="font-semibold" style={{ color: TEXT_PRIMARY }}>{invite.senderName}</span> {t('has invited you to a draft!')}
        </div>
        <div className="p-3 rounded mb-3" style={{ background: 'rgba(0,0,0,0.25)', border: `1px solid ${GOLD} 0.12)` }}>
          <div className="text-sm font-semibold mb-2" style={{ color: TEXT_PRIMARY }}>{t('{set} Draft', { set: invite.setLabel })}</div>
          <div className="space-y-1 text-xs" style={{ color: TEXT_BODY }}>
            <div className="flex justify-between"><span style={{ color: TEXT_MUTED }}>{t('Start time')}</span><span>{startTime}</span></div>
            <div className="flex justify-between"><span style={{ color: TEXT_MUTED }}>{t('Players')}</span><span>{invite.playerCount}/{invite.podSize}</span></div>
            <div className="flex justify-between"><span style={{ color: TEXT_MUTED }}>{t('Entry cost')}</span>{(this.props.profile?.keystones || 0) >= keystoneCostForMode(invite.mode) ? (
              <span className="flex items-center gap-0.5" style={{ color: '#c9a86a' }} title={keystoneCostForMode(invite.mode) === 1 ? t('Free entry. 1 Keystone will be spent') : t('Free entry. {n} Keystones will be spent', { n: keystoneCostForMode(invite.mode) })}>{keystoneCostForMode(invite.mode)} <KeystoneIcon size={10} /></span>
            ) : (
              <span style={{ color: COIN_COLOR }}>{invite.entryCost} <CoinIcon size={10} /></span>
            )}</div>
          </div>
        </div>
        {accepted ? (
          <div className="text-xs text-center py-2" style={{ color: '#6dba6d' }}>{t('Joined! Opening draft view...')}</div>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              className="flex-1 py-2 text-xs font-semibold transition-all cursor-pointer rounded"
              style={{ ...GOLD_BTN, backgroundColor: '#3a2812' }}
              data-sound={UI.CONFIRM}
              disabled={accepting}
              onClick={() => this.handleAcceptDraftInvite(mail.id, invite.eventId, invite.mode)}
            >
              {accepting ? t('Joining...') : t('Accept & Join')}
            </button>
            <button
              type="button"
              className="py-2 px-4 text-xs transition-all cursor-pointer rounded"
              style={{ ...BEVELED_BTN, backgroundColor: '#0e0a06', color: TEXT_BODY }}
              data-sound={UI.CANCEL}
              onClick={() => this.handleDeclineDraftInvite(mail.id, invite.eventId)}
            >
              {t('Decline')}
            </button>
          </div>
        )}
      </div>
    );
  }

  handleAcceptDraftInvite = async (mailId, eventId, mode) => {
    this.setState({ draftInviteAccepting: mailId });
    try {
      const { joinDraftEvent } = await import('../utils/arena/draftApi');
      // Auto-prefer Keystones when the player holds enough for THIS mode
      // (sealed costs 2, draft costs 1). Matches the openDraftLobby path
      // used by chat-invite accepts. Dropping the paymentMethod here made
      // every mailbox accept fall through to the server's 'coins' default,
      // so an invitee with keystones was still being charged gold.
      const paymentMethod = (this.props.profile?.keystones || 0) >= keystoneCostForMode(mode) ? 'keystone' : 'coins';
      await joinDraftEvent(eventId, { paymentMethod });
      this.setState({ draftInviteAccepting: null, draftInviteAccepted: mailId });
      // Navigate to draft view after a brief moment
      setTimeout(() => {
        this.props.onClose?.();
        this.props.onAcceptDraftInvite?.(eventId);
      }, 800);
    } catch (err) {
      this.setState({ draftInviteAccepting: null });
      console.error('[Mailbox] accept draft invite failed:', err);
    }
  };

  handleDeclineDraftInvite = async (mailId, eventId) => {
    try {
      // Tell the server first so the creator gets refunded when this
      // was the last pending invite (server's auto-cancel path). Done
      // before the mail delete so a transient network blip doesn't
      // leave the invite hanging server-side after the player has
      // already moved on locally.
      if (eventId) declineDraftEvent(eventId).catch(() => {});
      await deleteMail(mailId);
      this.backToList();
      this.loadInbox();
    } catch (err) {
      console.error('[Mailbox] decline draft invite failed:', err);
    }
  };

  renderDetailView() {
    const { sorceryCards } = this.props;
    const { selectedMail: m, claiming } = this.state;
    if (!m) return null;

    const cards = m.attachedCards || m.cards || [];
    const coins = m.attachedCoins || m.coins || 0;
    const hasAttachments = cards.length > 0 || coins > 0;
    const canClaim = !m.claimed && hasAttachments;
    const isFriend = (m.type || 'friend') === 'friend';

    const claimedSlots = this.state.claimedSlots || new Set();
    // Once claimed on server, treat ALL slots as collected
    const effectiveClaimed = m.claimed ? true : false;
    const allSlotsClaimed = canClaim && cards.every((_, i) => claimedSlots.has(`card-${i}`)) && (coins <= 0 || claimedSlots.has('coins'));

    return (
      <div className="relative flex flex-col h-full">
        {/* Header */}
        <div className="pt-3 pb-2 shrink-0" style={{ paddingLeft: ROW_PADDING_X, paddingRight: ROW_PADDING_X }}>
          <div className="mb-2">
            <BackButton title={t('Back to inbox')} onClick={this.backToList} style={{ width: '1.75cqw', height: '1.75cqw' }} size={14} />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold" style={{ color: TEXT_PRIMARY }}>
                {m.senderName || t('System')}
              </div>
              <div className="text-[0.625cqw] mt-0.5" style={{ color: TEXT_MUTED }}>
                {timeAgo(m.createdAt)}
              </div>
            </div>
          </div>
          {m.subject && (
            <div className="text-xs font-medium mt-2" style={{ color: ACCENT_GOLD }}>
              {m.subject}
            </div>
          )}
        </div>

        <OrnamentalDivider className="my-1 shrink-0" />

        {/* Body + Attachments (scrollable together) */}
        <div className="flex-1 overflow-y-auto py-2 min-h-0" style={{ paddingLeft: ROW_PADDING_X, paddingRight: ROW_PADDING_X }}>
          {m.type === 'draft-invite' ? this.renderDraftInviteBody(m)
            : m.body && (m.type === 'news'
              ? <div className="mb-3"><MarkdownNotes text={m.body} /></div>
              : <div className="text-xs leading-relaxed whitespace-pre-wrap mb-3" style={{ color: TEXT_BODY }}>{m.body}</div>
            )}

          {/* Season-voucher mails are claimed via the portrait-pack
              picker rather than the standard attachments grid: no
              coins or cards to collect, just one click to open the
              chooser. Renders the CTA above the (empty) attachments
              area so the mail still ends on a clear action. */}
          {m.type === 'season-voucher' && !m.claimed ? (
            <div className="py-3 mt-2" style={{ borderTop: `1px solid ${GOLD} 0.08)` }}>
              <div className="text-[0.5625cqw] font-semibold mb-2" style={{ color: `${GOLD} 0.45)` }}>
                {t('Portrait Voucher. Click to redeem')}
              </div>
              <button
                type="button"
                onClick={() => this.setState({ voucherPickerMailId: m.id })}
                className="w-full px-3 py-2 transition-all cursor-pointer text-[0.6875cqw] font-bold"
                style={{
                  background: `linear-gradient(180deg, rgba(212,168,67,0.22) 0%, rgba(180,140,60,0.1) 100%)`,
                  border: `1px solid ${GOLD} 0.55)`,
                  borderRadius: 'var(--radius-md)',
                  color: ACCENT_GOLD,
                  boxShadow: 'inset 0 1px 0 rgba(255,235,195,0.12), 0 0 12px rgba(212,168,67,0.15)',
                  textShadow: '0 1px 2px rgba(0,0,0,0.55)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = `linear-gradient(180deg, rgba(212,168,67,0.34) 0%, rgba(180,140,60,0.18) 100%)`;
                  e.currentTarget.style.borderColor = `${GOLD} 0.8)`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = `linear-gradient(180deg, rgba(212,168,67,0.22) 0%, rgba(180,140,60,0.1) 100%)`;
                  e.currentTarget.style.borderColor = `${GOLD} 0.55)`;
                }}
              >
                {t('Choose Your Portrait Pack')}
              </button>
            </div>
          ) : null}
          {m.type === 'season-voucher' && m.claimed ? (
            <div className="py-3 mt-2 text-[0.625cqw]" style={{ color: TEXT_MUTED, borderTop: `1px solid ${GOLD} 0.08)` }}>
              {t('Voucher already redeemed.')}
            </div>
          ) : null}

          {/* Attachment grid. Inline after text */}
          {hasAttachments && (
            <div className="py-3 mt-2" style={{ borderTop: `1px solid ${GOLD} 0.08)` }}>
            <div className="text-[0.5625cqw] font-semibold mb-2" style={{ color: `${GOLD} 0.45)` }}>
              {effectiveClaimed ? t('Collected') : t('Attachments. Click to collect')}
            </div>
            <div className="grid grid-cols-5 gap-1.5">
              {cards.map((cardId, i) => {
                const resolvedId = typeof cardId === 'string' ? cardId : cardId?.cardId || '';
                const artGroup = typeof cardId === 'string' ? ART_GROUP_BASE : cardId?.artGroup;
                const imgUrl = resolveCardImage(resolvedId, artGroup, sorceryCards);
                const name = resolveCardName(resolvedId, artGroup, sorceryCards);
                const slotKey = `card-${i}`;
                const collected = effectiveClaimed || claimedSlots.has(slotKey);
                return (
                  <button
                    key={`${resolvedId}-${i}`}
                    type="button"
                    disabled={effectiveClaimed || collected}
                    className="relative rounded-card overflow-hidden transition-all duration-200"
                    style={{
                      border: `1px solid ${collected ? `${GOLD} 0.15)` : `${GOLD} 0.12)`}`,
                      opacity: collected ? 0.3 : 1,
                      cursor: (effectiveClaimed || collected) ? 'default' : 'pointer',
                      transform: collected && !effectiveClaimed ? 'scale(0.85)' : 'scale(1)',
                      filter: collected ? 'grayscale(0.8)' : 'none',
                    }}
                    onMouseEnter={(e) => {
                      if (!collected && !effectiveClaimed) {
                        e.currentTarget.style.transform = 'scale(1.08)';
                        e.currentTarget.style.borderColor = ACCENT_GOLD;
                        e.currentTarget.style.boxShadow = `0 0 12px ${GOLD} 0.3)`;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!collected && !effectiveClaimed) {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.borderColor = `${GOLD} 0.12)`;
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}
                    onClick={() => {
                      if (effectiveClaimed || collected) return;
                      this.handleSlotCollect(m, slotKey, { type: 'card', name, imgUrl }, cards, coins > 0);
                    }}
                  >
                    {imgUrl ? (
                      <img src={imgUrl} alt={name} className="w-full aspect-[63/88] object-cover" />
                    ) : (
                      <div className="w-full aspect-[63/88] flex items-center justify-center text-[0.5cqw]" style={{ background: `${GOLD} 0.04)`, color: TEXT_MUTED }}>
                        {name}
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 text-[0.4375cqw] text-center py-0.5 truncate" style={{ background: 'rgba(0,0,0,0.8)', color: TEXT_BODY }}>
                      {name}
                    </div>
                    {collected && !effectiveClaimed && (
                      <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.6)' }}>
                        <span className="text-sm" style={{ color: ACCENT_GOLD }}>✓</span>
                      </div>
                    )}
                  </button>
                );
              })}
              {coins > 0 && (
                <button
                  type="button"
                  disabled={effectiveClaimed || claimedSlots.has('coins')}
                  className="relative rounded-card overflow-hidden flex flex-col items-center justify-center gap-1 transition-all duration-200"
                  style={{
                    border: `1px solid ${(effectiveClaimed || claimedSlots.has('coins')) ? `${GOLD} 0.15)` : `${GOLD} 0.12)`}`,
                    background: `${GOLD} 0.04)`,
                    opacity: (effectiveClaimed || claimedSlots.has('coins')) ? 0.3 : 1,
                    cursor: (effectiveClaimed || claimedSlots.has('coins')) ? 'default' : 'pointer',
                    aspectRatio: '63 / 88',
                    transform: (claimedSlots.has('coins') && !effectiveClaimed) ? 'scale(0.85)' : 'scale(1)',
                    filter: claimedSlots.has('coins') ? 'grayscale(0.8)' : 'none',
                  }}
                  onMouseEnter={(e) => {
                    if (!claimedSlots.has('coins') && !effectiveClaimed) {
                      e.currentTarget.style.transform = 'scale(1.08)';
                      e.currentTarget.style.borderColor = ACCENT_GOLD;
                      e.currentTarget.style.boxShadow = `0 0 12px ${GOLD} 0.3)`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!claimedSlots.has('coins') && !effectiveClaimed) {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.borderColor = `${GOLD} 0.12)`;
                      e.currentTarget.style.boxShadow = 'none';
                    }
                  }}
                  onClick={() => {
                    if (effectiveClaimed || claimedSlots.has('coins')) return;
                    this.handleSlotCollect(m, 'coins', { type: 'coins', amount: coins }, cards, true);
                  }}
                >
                  <CoinIcon size={26} />
                  <span className="text-xs font-bold" style={{ color: COIN_COLOR }}>{coins}</span>
                  <span className="text-[0.4375cqw]" style={{ color: TEXT_MUTED }}>{t('gold')}</span>
                  {claimedSlots.has('coins') && !effectiveClaimed && (
                    <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.6)' }}>
                      <span className="text-sm" style={{ color: ACCENT_GOLD }}>✓</span>
                    </div>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
        </div>


        {/* Collection animation overlay */}
        <AnimatePresence>
          {this.state.collectAnim && (
            <motion.div
              className="absolute inset-0 z-20 flex flex-col items-center justify-center pointer-events-none"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              style={{ background: 'rgba(0,0,0,0.65)' }}
            >
              {this.state.collectAnim.type === 'card' && this.state.collectAnim.imgUrl ? (
                <motion.img
                  src={this.state.collectAnim.imgUrl}
                  alt=""
                  className="rounded-card"
                  initial={{ scale: 0.3, opacity: 0 }}
                  animate={{ scale: [0.3, 1.15, 0.95, 1.05, 1], opacity: 1 }}
                  exit={{ scale: 0.7, opacity: 0 }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                  style={{
                    width: '40%',
                    maxWidth: '10cqw',
                    aspectRatio: '63 / 88',
                    objectFit: 'cover',
                    boxShadow: `0 0 40px ${GOLD} 0.4), 0 20px 60px rgba(0,0,0,0.6)`,
                    border: `2px solid ${ACCENT_GOLD}`,
                  }}
                />
              ) : this.state.collectAnim.type === 'coins' ? (
                <motion.span
                  className="w-16 h-16 rounded-full"
                  initial={{ scale: 0.3, opacity: 0 }}
                  animate={{ scale: [0.3, 1.15, 0.95, 1.05, 1], opacity: 1 }}
                  exit={{ scale: 0.7, opacity: 0 }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                  style={{
                    background: `radial-gradient(circle at 35% 35%, #ffe680, ${COIN_COLOR}, #b8860b)`,
                    boxShadow: `0 0 40px ${GOLD} 0.5), 0 20px 60px rgba(0,0,0,0.6)`,
                    border: `2px solid ${ACCENT_GOLD}`,
                  }}
                />
              ) : null}
              <motion.div
                className="mt-4 text-sm font-bold arena-heading tracking-wide"
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -8, opacity: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                style={{
                  color: ACCENT_GOLD,
                  textShadow: `0 0 20px ${GOLD} 0.5), 0 2px 4px rgba(0,0,0,0.8)`,
                }}
              >
                {this.state.collectAnim.type === 'coins'
                  ? t('+ {n} Gold', { n: this.state.collectAnim.amount })
                  : t('Received: {name}', { name: this.state.collectAnim.name })
                }
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Delete confirmation modal */}
        {this.state.confirmDeleteMail?.id === m.id && (
          <div className="absolute inset-0 z-10 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)', borderRadius: 'inherit' }}>
            <div className="p-4 mx-4 rounded-lg" style={{ background: PANEL_BG, border: `1px solid ${GOLD} 0.3)` }}>
              <div className="text-xs font-semibold mb-2" style={{ color: TEXT_PRIMARY }}>
                {t('This letter has uncollected attachments')}
              </div>
              <div className="text-[0.6875cqw] mb-3" style={{ color: TEXT_MUTED }}>
                {(() => {
                  const cardsLabel = cards.length === 1 ? t('1 card') : t('{n} cards', { n: cards.length });
                  if (cards.length > 0 && coins > 0) return t('{cards} and {coins} gold will be lost forever. Are you sure?', { cards: cardsLabel, coins });
                  if (cards.length > 0) return t('{cards} will be lost forever. Are you sure?', { cards: cardsLabel });
                  return t('{coins} gold will be lost forever. Are you sure?', { coins });
                })()}
              </div>
              <div className="flex items-center gap-2 justify-end">
                <button
                  type="button"
                  className="px-3 py-1 text-[0.625cqw] cursor-pointer transition-all"
                  style={{ ...MAILBOX_BEVELED_BTN, color: TEXT_BODY, borderRadius: 'var(--radius-md)' }}
                  data-sound={UI.CANCEL}
                  onClick={() => this.setState({ confirmDeleteMail: null })}
                >
                  {t('Cancel')}
                </button>
                <button
                  type="button"
                  className="px-3 py-1 text-[0.625cqw] cursor-pointer transition-all"
                  style={MAILBOX_DANGER_BTN}
                  data-sound={UI.CANCEL}
                  onClick={() => {
                    this.setState({ confirmDeleteMail: null });
                    this.handleDelete(m);
                  }}
                >
                  {t('Delete Anyway')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  renderComposeView() {
    const { friendListData, profile, sorceryCards } = this.props;
    const {
      composeRecipient, composeSubject, composeBody,
      composeCards, composeCoins, showCardPicker, sending,
    } = this.state;

    const friends = friendListData?.friends || [];
    const collection = profile?.collection || [];
    const canSend = composeRecipient && (composeSubject.trim() || composeBody.trim());

    // Friends list mapped into the shared Select component's option
    // shape. The Select handles searchable typeahead, arrow-key
    // navigation, Enter-to-pick, and click selection. All the
    // autocomplete behavior we need lives in one shared component.
    const recipientOptions = friends.map((f) => ({ value: f.id, label: f.name || t('Unknown') }));

    return (
      <div className="flex flex-col h-full">
        <div className="pt-3 pb-2" style={{ paddingLeft: ROW_PADDING_X, paddingRight: ROW_PADDING_X }}>
          <div className="mb-2">
            <BackButton title={t('Back to inbox')} onClick={this.backToList} style={{ width: '1.75cqw', height: '1.75cqw' }} size={14} />
          </div>
          <div className="text-sm font-semibold" style={{ color: TEXT_PRIMARY }}>
            {t('New Message')}
          </div>
        </div>

        <OrnamentalDivider className="my-1" />

        <div className="flex-1 overflow-hidden py-2 flex flex-col gap-2.5" style={{ paddingLeft: ROW_PADDING_X, paddingRight: ROW_PADDING_X }}>
          {/* Recipient. Shared shadcn Select with searchable typeahead.
              Click the field to open the dropdown, type to filter,
              arrow keys / Enter to pick. All autocomplete behavior is
              encapsulated in src/components/ui/select.jsx. */}
          <div>
            <label className="text-[0.625cqw] font-semibold block mb-1" style={{ color: `${GOLD} 0.55)` }}>
              {t('To')}
            </label>
            {friends.length === 0 ? (
              <div
                className="text-[0.6875cqw] px-2.5 py-2 text-center rounded-md"
                style={{ background: SOLID_BASE, border: `1px solid ${GOLD} 0.18)`, color: TEXT_MUTED }}
              >
                {t('No friends to send to')}
              </div>
            ) : (
              <Select
                ariaLabel={t('Recipient')}
                options={recipientOptions}
                value={composeRecipient?.id || ''}
                onValueChange={(id) => {
                  const friend = friends.find((f) => f.id === id);
                  this.setState({ composeRecipient: friend || null });
                }}
                placeholder={t('Choose a friend...')}
                searchable
                menuSearchPlaceholder={t('Type to filter friends...')}
                noOptionsMessage={t('No matching friends')}
              />
            )}
          </div>

          {/* Subject */}
          <div>
            <label className="text-[0.625cqw] font-semibold block mb-1" style={{ color: `${GOLD} 0.55)` }}>
              {t('Subject')}
            </label>
            <input
              type="text"
              maxLength={100}
              value={composeSubject}
              placeholder={t('Subject...')}
              className="w-full px-2.5 py-1.5 text-xs outline-none"
              style={{ ...MAILBOX_INPUT_STYLE, borderRadius: ROW_RADIUS, color: TEXT_PRIMARY }}
              onInput={e => this.setState({ composeSubject: e.target.value })}
            />
          </div>

          {/* Body */}
          <div>
            <label className="text-[0.625cqw] font-semibold block mb-1" style={{ color: `${GOLD} 0.55)` }}>
              {t('Message')}
            </label>
            <textarea
              maxLength={256}
              rows={3}
              value={composeBody}
              placeholder={t('Write your message...')}
              className="w-full px-2.5 py-1.5 text-xs outline-none resize-none"
              style={{ ...MAILBOX_INPUT_STYLE, borderRadius: ROW_RADIUS, color: TEXT_PRIMARY }}
              onInput={e => this.setState({ composeBody: e.target.value })}
            />
            <div className="text-[0.5625cqw] text-right mt-0.5" style={{ color: TEXT_MUTED }}>
              {composeBody.length}/256
            </div>
          </div>

          {/* Attach Cards */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[0.625cqw] font-semibold" style={{ color: `${GOLD} 0.55)` }}>
                {composeCards.length > 0 ? t('Attach Cards ({n}/10)', { n: composeCards.length }) : t('Attach Cards')}
              </label>
              <button
                type="button"
                className="text-[0.625cqw] cursor-pointer transition-all"
                style={{ color: ACCENT_GOLD }}
                onClick={() => this.setState(s => ({ showCardPicker: !s.showCardPicker, cardPickerSearch: '' }))}
              >
                {showCardPicker ? t('Close Picker') : t('Choose Cards')}
              </button>
            </div>
            {composeCards.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {composeCards.map((entry, i) => {
                  const { cardId, artGroup, foiling } = entry;
                  const imgUrl = resolveCardImage(cardId, artGroup, sorceryCards);
                  const name = resolveCardName(cardId, artGroup, sorceryCards);
                  const isFoil = foiling === 'F' || foiling === 'R';
                  const borderColor = foiling === 'R' ? '#c480e0' : ACCENT_GOLD;
                  return (
                    <div
                      key={`${cardId}-${artGroup}-${foiling}-${i}`}
                      className="relative rounded-card overflow-hidden cursor-pointer"
                      style={{ border: `1px solid ${borderColor}`, width: '2.25cqw' }}
                      title={t('{name}. Click to remove', { name: isFoil ? `${name} (${foiling === 'R' ? t('Rainbow Foil') : t('Foil')})` : name })}
                      onClick={() => this.removeCardFromCompose(cardId, artGroup, foiling)}
                    >
                      {imgUrl ? (
                        <img src={imgUrl} alt={name} className="w-full aspect-[5/7] object-cover" />
                      ) : (
                        <div className="w-full aspect-[5/7] flex items-center justify-center text-[0.375cqw]" style={{ background: `${GOLD} 0.08)`, color: TEXT_MUTED }}>
                          {name}
                        </div>
                      )}
                      {isFoil && (
                        <span
                          className="absolute top-0 right-0 text-[0.375cqw] font-bold px-0.5 rounded-bl"
                          style={{ background: 'rgba(0,0,0,0.85)', color: borderColor }}
                        >
                          {foiling === 'R' ? 'RF' : 'F'}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Coins */}
          <div>
            <label className="text-[0.625cqw] font-semibold block mb-1" style={{ color: `${GOLD} 0.55)` }}>
              {t('Attach Coins')}
            </label>
            <div className="flex items-center gap-2">
              <CoinIcon size={14} />
              <input
                type="number"
                min="0"
                max={profile?.coins || 0}
                value={composeCoins || ''}
                placeholder="0"
                className="w-24 px-2.5 py-1.5 text-xs outline-none"
                style={{ ...MAILBOX_INPUT_STYLE, borderRadius: ROW_RADIUS, color: TEXT_PRIMARY }}
                onInput={e => {
                  const val = Math.max(0, Math.min(parseInt(e.target.value, 10) || 0, profile?.coins || 0));
                  this.setState({ composeCoins: val });
                }}
              />
              <span className="text-[0.625cqw]" style={{ color: TEXT_MUTED }}>
                {t('/ {n} available', { n: profile?.coins || 0 })}
              </span>
            </div>
          </div>
        </div>

      </div>
    );
  }

  render() {
    const { open, onClose } = this.props;
    const { view, error } = this.state;

    // Panel stays mounted across close/open cycles so the user's tab
    // choice, selected mail, selected friend row, chat list scroll
    // position. Everything. Survives. Visibility is driven by
    // animated opacity + pointerEvents instead of AnimatePresence's
    // mount/unmount toggle. Cheap: one always-present DOM tree, no
    // listeners or work are thrown away when the user hits Esc/O.
    return (
      <>
        {/* No backdrop locally. The app-level blurred backdrop in
            app.jsx handles that and is the thing that actually
            responds to outside clicks. */}
        <motion.div
          className="fixed z-[60] flex flex-col"
          data-screenshot-ignore="true"
          initial={false}
          animate={{
            opacity: open ? 1 : 0,
            x: open ? 0 : 16,
            y: '-50%',
          }}
          transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
          style={{
            right: '1cqw',
            top: '50%',
            width: '23.75cqw',
            height: '60cqh',
            pointerEvents: open ? 'auto' : 'none',
            // Carved-stone material, opaque base. The panel floats over
            // a live hub screen, so a translucent base would show the
            // board through the stone.
            ...OPAQUE_TEXTURED_PANEL_STYLE,
            borderRadius: PANEL_RADIUS,
            // No top padding: the tab rail is the panel's header and
            // takes its own vertical rhythm. Everything else floats on
            // the panel's padding instead of running into its rim.
            padding: `0 ${PANEL_PADDING} ${PANEL_PADDING}`,
          }}
          onClick={e => e.stopPropagation()}
        >
          <FourCorners color={`${GOLD} 0.55)`} size="1.7cqw" />
          {/* Messages tab carries the standing-figure ornament that the
              pre-O-dashboard FriendsSidebar used. Keeps the social
              surface visually distinct from the other tabs, which stay
              on the intertwined broa016 centerpiece. */}
          <VikingOrnament ornament={this.state.tab === 'friend' ? 'figurative005' : 'broa016'} variant="centerpiece" opacity={0.03} />

          {/* Tab rail is the panel header. No title bar, no X button.
              The O hotkey and app-level backdrop click drive close,
              so a redundant close control would just steal vertical
              space. Dropping the title makes the tabs read as the
              primary navigation at the very top. */}
          {this.renderTabBar()}

          {view === 'list' && error && (
            <div className="my-1.5 px-2.5 py-1.5 text-[0.625cqw] shrink-0" style={{ borderRadius: ROW_RADIUS, background: 'rgba(180,60,60,0.08)', border: '1px solid rgba(180,60,60,0.25)', color: '#c45050' }}>
              {error}
            </div>
          )}

          {/* Single-pane body. Messages tab shows the conversation list;
              clicking a friend calls onOpenChat so the parent can mount
              a separate ChatPanel alongside this one (Discord-style
              independent panels). The Notifications tab opens a letter
              in place with list → detail navigation inside the panel. */}
          {/* Second beat of the open. The rail lands with the panel,
              the contents follow ~80ms behind and settle up by 8px, so
              the panel reads as a surface that fills rather than one
              flat block fading in. Closing runs the same 8px in
              reverse with no delay, so the dismissal stays quicker and
              quieter than the open. */}
          <motion.div
            className="flex-1 min-h-0 flex flex-col overflow-hidden"
            initial={false}
            animate={{ opacity: open ? 1 : 0, y: open ? 0 : 8 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1], delay: open ? 0.08 : 0 }}
          >
            {this.renderPanelBody()}
          </motion.div>

          {/* Per-pane footer buttons for mail detail and compose */}
          {this.renderPanelFooter()}

          {/* Card picker. Anchored to left of this panel */}
          <AnimatePresence>
            {this.state.showCardPicker ? this.renderCardPicker() : null}
          </AnimatePresence>
        </motion.div>

        {/* Season-voucher picker opens from a mail's detail view; it
            lives at the panel root (not inside renderCardPicker) so it
            doesn't depend on the compose card picker being open. */}
        {this.state.voucherPickerMailId ? (
          <SeasonVoucherPickerModal
            mailId={this.state.voucherPickerMailId}
            sorceryCards={this.props.sorceryCards}
            onClose={() => this.setState({ voucherPickerMailId: null })}
            onRedeemed={() => {
              this.setState((s) => {
                const mailId = s.voucherPickerMailId;
                const updatedMail = s.mail.map(m => m.id === mailId ? { ...m, claimed: true } : m);
                const updatedSelected = s.selectedMail?.id === mailId ? { ...s.selectedMail, claimed: true } : s.selectedMail;
                return {
                  voucherPickerMailId: null,
                  mail: updatedMail,
                  selectedMail: updatedSelected,
                };
              });
            }}
          />
        ) : null}
      </>
    );
  }

  renderPanelBody() {
    const { tab, view, selectedMail } = this.state;

    if (tab === 'global') {
      return (
        <GlobalChatView
          compact
          profile={this.props.profile}
          sorceryCards={this.props.sorceryCards}
          friends={this.props.friendListData?.friends}
          onViewProfile={this.props.onViewProfile}
          canModerate={this.props.canModerateChat}
          highlightId={this.props.focusGlobalMessageId}
          onWatchReplay={this.props.onWatchReplay}
          onOpenCard={(card) => {
            playUI(UI.INSPECTOR_OPEN);
            this.setState({ inspectedCard: { card, printing: card.printings?.[0] || null, rarity: card.rarity } });
          }}
        />
      );
    }

    if (tab === 'friend') {
      return (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <ChatConversationList
            ref={(ref) => { this.chatListRef = ref; }}
            activeFriendId={this.props.activeChatFriendId}
            myUserId={this.props.profile?.id}
            onSelectFriend={(friend) => {
              // Hand off to the parent. SocialPanel no longer owns the
              // chat view. ChatPanel is a separate floating panel.
              this.props.onOpenChat?.(friend);
            }}
            friendListData={this.props.friendListData}
            sorceryCards={this.props.sorceryCards}
          />
        </div>
      );
    }

    if (view === 'compose') {
      return <div className="flex-1 min-h-0 overflow-y-auto">{this.renderComposeView()}</div>;
    }
    if (view === 'detail' && selectedMail) {
      return <div className="flex-1 min-h-0 overflow-y-auto">{this.renderDetailView()}</div>;
    }
    return (
      <div className="flex-1 min-h-0 overflow-y-auto">
        <NotificationsFeed
          active={this.props.open && tab === 'notifications'}
          profile={this.props.profile}
          mail={this.state.mail}
          onOpenMail={this.openDetail}
        />
      </div>
    );
  }

  renderPanelFooter() {
    const { view, selectedMail, error } = this.state;
    if (view === 'detail' && selectedMail) {
      const m = selectedMail;
      const cards = m.attachedCards || m.cards || [];
      const coins = m.attachedCoins || m.coins || 0;
      const hasAtt = cards.length > 0 || coins > 0;
      return (
        <div className="shrink-0 pt-2 flex items-center justify-end gap-2" style={{ borderTop: `1px solid ${GOLD} 0.12)` }}>
          <button
            type="button"
            className="px-3 py-1.5 text-[0.625cqw] cursor-pointer transition-all"
            style={MAILBOX_DANGER_BTN}
            data-sound={UI.CANCEL}
            onClick={() => {
              if (hasAtt && !m.claimed) this.setState({ confirmDeleteMail: m });
              else this.handleDelete(m);
            }}
          >
            {t('Delete')}
          </button>
        </div>
      );
    }
    if (view === 'compose') {
      return (
        <div className="shrink-0 pt-2 flex items-center justify-end gap-2" style={{ borderTop: `1px solid ${GOLD} 0.12)` }}>
          {error ? (
            <div className="flex-1 text-[0.625cqw] truncate" style={{ color: '#c45050' }}>{error}</div>
          ) : null}
          <button
            type="button"
            disabled={!(this.state.composeRecipient && (this.state.composeSubject.trim() || this.state.composeBody.trim())) || this.state.sending}
            className="px-5 py-1.5 text-[0.6875cqw] font-semibold cursor-pointer transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ ...MAILBOX_GOLD_BTN, borderRadius: 'var(--radius-md)' }}
            data-sound={UI.CONFIRM}
            onClick={this.handleSend}
          >
            {this.state.sending ? t('Sending...') : t('Send')}
          </button>
        </div>
      );
    }
    return null;
  }

  renderCardPicker() {
    const { profile, sorceryCards } = this.props;
    const { composeCards, cardPickerSearch } = this.state;
    const collection = profile?.collection || [];

    // Each (cardId, artGroup, foiling) row becomes its own tile so the sender
    // can pick the exact copy. The foil, or the alt art. The collection from
    // the server is already structured this way.
    const searchLower = cardPickerSearch.toLowerCase();
    const filtered = searchLower
      ? collection.filter(entry => {
          const name = resolveCardName(entry.cardId, entry.artGroup, sorceryCards);
          return name.toLowerCase().includes(searchLower);
        })
      : collection;

    return (
      <>
      {/* Backdrop. Click anywhere outside the picker to close it */}
      <div className="fixed inset-0 z-[58]" onClick={() => this.setState({ showCardPicker: false, inspectedCard: null, pickerHoveredEntry: null })} />
      <motion.div
        key="card-picker"
        className="absolute flex flex-col z-[59]"
        initial={{ opacity: 0, x: 12, scale: 0.96 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        exit={{ opacity: 0, x: 12, scale: 0.96 }}
        transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
        style={{
          top: 0,
          right: '100%',
          marginRight: '0.5cqw',
          width: '20cqw',
          height: '100%',
          transformOrigin: 'top right',
          ...OPAQUE_TEXTURED_PANEL_STYLE,
          borderRadius: PANEL_RADIUS,
        }}
        onClick={e => e.stopPropagation()}
      >
        <FourCorners color={`${GOLD} 0.55)`} size="1.7cqw" />

        {/* Header */}
        <div className="pt-3 pb-2" style={{ paddingLeft: ROW_PADDING_X, paddingRight: ROW_PADDING_X }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold arena-heading" style={{ color: TEXT_PRIMARY }}>
              {t('Attach Cards')}
            </span>
            <CloseButton
              title={t('Close picker')}
              onClick={() => this.setState({ showCardPicker: false, inspectedCard: null, pickerHoveredEntry: null })}
              style={{ width: '1.5cqw', height: '1.5cqw' }}
              size={12}
            />
          </div>
          <input
            type="text"
            placeholder={t('Search cards...')}
            value={cardPickerSearch}
            className="w-full px-2.5 py-1.5 text-xs outline-none"
            style={{ ...MAILBOX_INPUT_STYLE, borderRadius: ROW_RADIUS, color: TEXT_PRIMARY }}
            onInput={e => this.setState({ cardPickerSearch: e.target.value })}
          />
        </div>

        <OrnamentalDivider className="my-1" />

        {/* Card grid */}
        <div className="flex-1 overflow-y-auto py-2" style={{ paddingLeft: ROW_PADDING_X, paddingRight: ROW_PADDING_X }}>
          <div className="grid grid-cols-4 gap-2">
            {filtered.map(entry => {
              const foiling = entry.foiling || 'S';
              const artGroup = entry.artGroup || ART_GROUP_BASE;
              const imgUrl = resolveCardImage(entry.cardId, artGroup, sorceryCards);
              const name = resolveCardName(entry.cardId, artGroup, sorceryCards);
              const groupLabel = artGroupLabel(artGroup);
              const chatCards = this.chatViewRef?.state?.attachCards || [];
              const activeCards = this.state.chatFriend ? chatCards : composeCards;
              const selectedCount = activeCards.filter(
                c => c.cardId === entry.cardId
                  && (c.artGroup || ART_GROUP_BASE) === artGroup
                  && c.foiling === foiling
              ).length;
              const isSelected = selectedCount > 0;
              const canAddMore = selectedCount < entry.quantity && activeCards.length < 10;
              const isFoil = foiling === 'F' || foiling === 'R';
              const foilColor = foiling === 'R' ? '#c480e0' : ACCENT_GOLD;
              return (
                <div key={`${entry.cardId}-${artGroup}-${foiling}`} className="relative">
                  <button
                    type="button"
                    className="relative w-full rounded-card overflow-hidden transition-all cursor-pointer"
                    style={isSelected
                      ? { border: `2px solid ${isFoil ? foilColor : ACCENT_GOLD}`, boxShadow: `0 0 10px ${GOLD} 0.25)` }
                      : { border: `2px solid ${isFoil ? foilColor + '55' : `${GOLD} 0.1)`}` }
                    }
                    onMouseEnter={e => {
                      if (!isSelected) e.currentTarget.style.borderColor = isFoil ? foilColor : `${GOLD} 0.3)`;
                      const card = sorceryCards?.find(c => c.unique_id === entry.cardId);
                      if (card) {
                        const printing = printingsInArtGroup(card, artGroup, foiling)[0]
                          || resolvePrinting(card, null, foiling);
                        this.setState({ pickerHoveredEntry: { card, printing, rarity: card.rarity } });
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isSelected) e.currentTarget.style.borderColor = isFoil ? foilColor + '55' : `${GOLD} 0.1)`;
                      this.setState({ pickerHoveredEntry: null });
                    }}
                    onClick={() => {
                      if (!canAddMore) return;
                      if (this.state.chatFriend && this.chatViewRef) {
                        this.chatViewRef.addCard(entry.cardId, artGroup, foiling);
                      } else {
                        this.addCardToCompose(entry.cardId, artGroup, foiling);
                      }
                    }}
                  >
                    {imgUrl ? (
                      <img src={imgUrl} alt={name} className="w-full aspect-[5/7] object-cover" />
                    ) : (
                      <div className="w-full aspect-[5/7] flex items-center justify-center text-[0.5cqw] p-1 text-center" style={{ background: `${GOLD} 0.04)`, color: TEXT_MUTED }}>
                        {name}
                      </div>
                    )}
                    <span
                      className="absolute top-1 right-1 text-[0.5cqw] font-bold px-1 py-0.5 rounded"
                      style={{ background: 'rgba(0,0,0,0.8)', color: TEXT_BODY }}
                    >
                      x{entry.quantity - selectedCount}
                    </span>
                    {isFoil && (
                      <span
                        className="absolute top-1 left-1 text-[0.5cqw] font-bold px-1 py-0.5 rounded"
                        style={{ background: 'rgba(0,0,0,0.8)', color: foilColor }}
                      >
                        {foiling === 'R' ? 'RF' : 'F'}
                      </span>
                    )}
                    {groupLabel && (
                      /* Two tiles of one card differ only by illustration, so
                         the alt one has to name its printing. */
                      <span
                        className="absolute left-1 text-[0.4375cqw] font-bold px-1 py-0.5 rounded truncate max-w-[70%]"
                        style={{ background: 'rgba(0,0,0,0.8)', color: ACCENT_GOLD, top: isFoil ? '1.6cqw' : '0.25cqw' }}
                      >
                        {groupLabel}
                      </span>
                    )}
                    {isSelected && (
                      <div className="absolute bottom-7 left-1 min-w-[1.125cqw] h-[1.125cqw] rounded flex items-center justify-center text-[0.5625cqw] font-bold" style={{ background: ACCENT_GOLD, color: '#1a1408', boxShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
                        {selectedCount}
                      </div>
                    )}
                    <div
                      className="absolute bottom-0 left-0 right-0 text-[0.4375cqw] text-center py-0.5 truncate px-0.5"
                      style={{ background: 'rgba(0,0,0,0.75)', color: TEXT_BODY }}
                    >
                      {name}
                    </div>
                  </button>
                  {isSelected && (
                    <button
                      type="button"
                      className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-[0.5625cqw] font-bold cursor-pointer z-10"
                      style={{ background: '#c45050', color: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.5)' }}
                      onClick={() => this.removeCardFromCompose(entry.cardId, foiling)}
                    >
                      −
                    </button>
                  )}
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="col-span-4 text-[0.625cqw] py-8 text-center" style={{ color: TEXT_MUTED }}>
                {cardPickerSearch ? t('No cards match your search') : t('No cards in collection')}
              </div>
            )}
          </div>
        </div>
      </motion.div>
      {this.state.inspectedCard && (
        <CardInspector
          card={this.state.inspectedCard.card}
          imageUrl={this.state.inspectedCard.printing?.image_url}
          rarity={this.state.inspectedCard.rarity}
          foiling={this.state.inspectedCard.printing?.foiling}
          onClose={() => { playUI(UI.INSPECTOR_CLOSE); this.setState({ inspectedCard: null }); }}
        />
      )}
      {/* SeasonVoucherPickerModal is rendered from the main render()
          instead of here so it can open from the mail detail view
          without the compose card picker being open. */}
      </>
    );
  }
}
